import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const steps = [
  { number: '1', title: 'Enter a Company Name', description: 'Type in the name of any entity you need to screen for export compliance.' },
  { number: '2', title: 'Automated Screening', description: 'Our AI cross-references multiple data sources, validates matches, and analyzes corporate structures.' },
  { number: '3', title: 'Get Your Report', description: 'Receive a comprehensive compliance screening with a downloadable PDF memorandum in seconds.' },
];

const features = [
  { title: 'Entity Resolution', description: 'AI-powered company enrichment identifies aliases, locations, and related entities to ensure thorough screening.' },
  { title: 'Corporate Structure', description: 'Automatically maps parent companies and subsidiaries to identify related screening obligations.' },
  { title: 'CSL Search', description: 'Searches the Consolidated Screening List across all U.S. government restricted party lists.' },
  { title: 'Match Validation', description: 'AI validates each potential match against the original query, reducing false positives.' },
  { title: 'Domain Intelligence', description: 'Analyzes company web domains for registration age, hosting details, and other due diligence factors.' },
  { title: 'PDF Reports', description: 'Generate professional compliance memoranda summarizing findings for your records.' },
];

const pricingFeatures = [
  'Unlimited screenings',
  'Corporate structure analysis',
  'Domain intelligence',
  'CSL + OpenSanctions screening',
  'Match validation',
  'PDF reports',
  'Screening history',
];

const LandingPage: React.FC = () => {
  return (
    <div className="landing-page">
      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <h1>Export Compliance Screening, Simplified</h1>
          <p className="landing-hero-subtitle">
            Screen companies against U.S. and international sanctions lists in seconds. AI-powered entity resolution, match validation, and compliance screening — all in one place.
          </p>
          <div className="landing-hero-ctas">
            <Link to="/register" className="landing-btn landing-btn-primary">Get Started</Link>
            <Link to="/login" className="landing-btn landing-btn-secondary">Sign In</Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="landing-section">
        <h2 className="landing-section-title">How It Works</h2>
        <div className="landing-steps">
          {steps.map((step) => (
            <div key={step.number} className="landing-step">
              <div className="landing-step-number">{step.number}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="landing-section landing-section-alt">
        <h2 className="landing-section-title">Features</h2>
        <div className="landing-features-grid">
          {features.map((feature) => (
            <div key={feature.title} className="landing-feature-card">
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="landing-section">
        <h2 className="landing-section-title">Pricing</h2>
        <div className="landing-pricing">
          <div className="landing-pricing-card">
            <h3>Full Access</h3>
            <div className="landing-pricing-price">
              $250<span className="landing-pricing-period">/month</span>
            </div>
            <p className="landing-pricing-annual">$2,500/year (save 17%)</p>
            <ul className="landing-pricing-features">
              {pricingFeatures.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
            <Link to="/register" className="landing-btn landing-btn-primary">Get Started</Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section landing-final-cta">
        <h2>Ready to streamline your export compliance?</h2>
        <p>Start screening companies in seconds.</p>
        <Link to="/register" className="landing-btn landing-btn-primary landing-btn-lg">Get Started</Link>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/process">How It Works</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/login">Sign In</Link>
          <Link to="/register">Sign Up</Link>
        </div>
        <p className="landing-footer-copy">&copy; {new Date().getFullYear()} CompanySieve. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
